function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t.return || t.return(); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
/**
 * @module ol/render/canvas/style
 */

import Circle from '../../style/Circle.js';
import Fill from '../../style/Fill.js';
import Icon from '../../style/Icon.js';
import RegularShape from '../../style/RegularShape.js';
import Stroke from '../../style/Stroke.js';
import Style from '../../style/Style.js';
import Text from '../../style/Text.js';
import { BooleanType, ColorType, NumberArrayType, NumberType, StringType, computeGeometryType, newParsingContext } from '../../expr/expression.js';
import { buildExpression, newEvaluationContext } from '../../expr/cpu.js';
import { isEmpty } from '../../obj.js';
import { toSize } from '../../size.js';

/**
 * @fileoverview This module includes functions to build styles for the canvas renderer.  Building
 * is composed of two steps: parsing and compiling.  The parsing step takes an encoded expression
 * and returns an instance of one of the expression classes.  The compiling step takes the
 * expression instance and returns a function that can be evaluated to return a literal value.  The
 * evaluator function should do as little allocation and work as possible.
 */

/**
 * @typedef {import("../../style/flat.js").FlatStyle} FlatStyle
 */

/**
 * @typedef {import("../../expr/expression.js").EncodedExpression} EncodedExpression
 */

/**
 * @typedef {import("../../expr/expression.js").ParsingContext} ParsingContext
 */

/**
 * @typedef {import("../../expr/expression.js").CallExpression} CallExpression
 */

/**
 * @typedef {import("../../expr/cpu.js").EvaluationContext} EvaluationContext
 */

/**
 * @typedef {import("../../expr/cpu.js").ExpressionEvaluator} ExpressionEvaluator
 */

/**
 * @param {EvaluationContext} context The evaluation context.
 * @return {boolean} Always true.
 */
function always(context) {
  return true;
}

/**
 * This function adapts a rule evaluator to the existing style function interface.
 * After we have deprecated the style function, we can use the compiled rules directly
 * and pass a more complete evaluation context (variables, zoom, time, etc.).
 *
 * @param {Array<import('../../style/flat.js').Rule>} rules The rules.
 * @return {import('../../style/Style.js').StyleFunction} A style function.
 */
export function rulesToStyleFunction(rules) {
  var parsingContext = newParsingContext();
  var evaluator = buildRuleSet(rules, parsingContext);
  var evaluationContext = newEvaluationContext();
  return function (feature, resolution) {
    evaluationContext.properties = feature.getPropertiesInternal();
    evaluationContext.resolution = resolution;
    if (parsingContext.featureId) {
      var id = feature.getId();
      if (id !== undefined) {
        evaluationContext.featureId = id;
      } else {
        evaluationContext.featureId = null;
      }
    }
    if (parsingContext.geometryType) {
      evaluationContext.geometryType = computeGeometryType(feature.getGeometry());
    }
    return evaluator(evaluationContext);
  };
}

/**
 * This function adapts a style evaluator to the existing style function interface.
 * After we have deprecated the style function, we can use the compiled rules directly
 * and pass a more complete evaluation context (variables, zoom, time, etc.).
 *
 * @param {Array<import('../../style/flat.js').FlatStyle>} flatStyles The flat styles.
 * @return {import('../../style/Style.js').StyleFunction} A style function.
 */
export function flatStylesToStyleFunction(flatStyles) {
  var parsingContext = newParsingContext();
  var length = flatStyles.length;

  /**
   * @type {Array<StyleEvaluator>}
   */
  var evaluators = new Array(length);
  for (var i = 0; i < length; ++i) {
    evaluators[i] = buildStyle(flatStyles[i], parsingContext);
  }
  var evaluationContext = newEvaluationContext();

  /**
   * @type {Array<Style>}
   */
  var styles = new Array(length);
  return function (feature, resolution) {
    evaluationContext.properties = feature.getPropertiesInternal();
    evaluationContext.resolution = resolution;
    if (parsingContext.featureId) {
      var id = feature.getId();
      if (id !== undefined) {
        evaluationContext.featureId = id;
      } else {
        evaluationContext.featureId = null;
      }
    }
    var nonNullCount = 0;
    for (var _i = 0; _i < length; ++_i) {
      var style = evaluators[_i](evaluationContext);
      if (style) {
        styles[nonNullCount] = style;
        nonNullCount += 1;
      }
    }
    styles.length = nonNullCount;
    return styles;
  };
}

/**
 * @typedef {function(EvaluationContext):Array<Style>} RuleSetEvaluator
 */

/**
 * @typedef {Object} CompiledRule
 * @property {ExpressionEvaluator} filter The compiled filter evaluator.
 * @property {Array<StyleEvaluator>} styles The list of compiled style evaluators.
 */

/**
 * @param {Array<import('../../style/flat.js').Rule>} rules The rules.
 * @param {ParsingContext} context The parsing context.
 * @return {RuleSetEvaluator} The evaluator function.
 */
export function buildRuleSet(rules, context) {
  var length = rules.length;

  /**
   * @type {Array<CompiledRule>}
   */
  var compiledRules = new Array(length);
  for (var i = 0; i < length; ++i) {
    var rule = rules[i];
    var filter = 'filter' in rule ? buildExpression(rule.filter, BooleanType, context) : always;

    /**
     * @type {Array<StyleEvaluator>}
     */
    var styles = void 0;
    if (Array.isArray(rule.style)) {
      var styleLength = rule.style.length;
      styles = new Array(styleLength);
      for (var j = 0; j < styleLength; ++j) {
        styles[j] = buildStyle(rule.style[j], context);
      }
    } else {
      styles = [buildStyle(rule.style, context)];
    }
    compiledRules[i] = {
      filter: filter,
      styles: styles
    };
  }
  return function (context) {
    /**
     * @type {Array<Style>}
     */
    var styles = [];
    var someMatched = false;
    for (var _i2 = 0; _i2 < length; ++_i2) {
      var filterEvaluator = compiledRules[_i2].filter;
      if (!filterEvaluator(context)) {
        continue;
      }
      if (rules[_i2].else && someMatched) {
        continue;
      }
      someMatched = true;
      var _iterator = _createForOfIteratorHelper(compiledRules[_i2].styles),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var styleEvaluator = _step.value;
          var style = styleEvaluator(context);
          if (!style) {
            continue;
          }
          styles.push(style);
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
    }
    return styles;
  };
}

/**
 * @typedef {function(EvaluationContext):Style|null} StyleEvaluator
 */

/**
 * @param {FlatStyle} flatStyle A flat style literal.
 * @param {ParsingContext} context The parsing context.
 * @return {StyleEvaluator} A function that evaluates to a style.  The style returned by
 * this function will be reused between invocations.
 */
export function buildStyle(flatStyle, context) {
  var evaluateFill = buildFill(flatStyle, '', context);
  var evaluateStroke = buildStroke(flatStyle, '', context);
  var evaluateText = buildText(flatStyle, context);
  var evaluateImage = buildImage(flatStyle, context);
  var evaluateZIndex = numberEvaluator(flatStyle, 'z-index', context);
  if (!evaluateFill && !evaluateStroke && !evaluateText && !evaluateImage && !isEmpty(flatStyle)) {
    // assume this is a user error
    // would be nice to check the properties and suggest "did you mean..."
    throw new Error('No fill, stroke, point, or text symbolizer properties in style: ' + JSON.stringify(flatStyle));
  }
  var style = new Style();
  return function (context) {
    var empty = true;
    if (evaluateFill) {
      var fill = evaluateFill(context);
      if (fill) {
        empty = false;
      }
      style.setFill(fill);
    }
    if (evaluateStroke) {
      var stroke = evaluateStroke(context);
      if (stroke) {
        empty = false;
      }
      style.setStroke(stroke);
    }
    if (evaluateText) {
      var text = evaluateText(context);
      if (text) {
        empty = false;
      }
      style.setText(text);
    }
    if (evaluateImage) {
      var image = evaluateImage(context);
      if (image) {
        empty = false;
      }
      style.setImage(image);
    }
    if (evaluateZIndex) {
      style.setZIndex(evaluateZIndex(context));
    }
    if (empty) {
      return null;
    }
    return style;
  };
}

/**
 * @typedef {function(EvaluationContext):Fill|null} FillEvaluator
 */

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} prefix The property prefix.
 * @param {ParsingContext} context The parsing context.
 * @return {FillEvaluator?} A function that evaluates to a fill.
 */
function buildFill(flatStyle, prefix, context) {
  var evaluateColor;
  if (prefix + 'fill-pattern-src' in flatStyle) {
    evaluateColor = patternEvaluator(flatStyle, prefix + 'fill-', context);
  } else {
    evaluateColor = colorLikeEvaluator(flatStyle, prefix + 'fill-color', context);
  }
  if (!evaluateColor) {
    return null;
  }
  var fill = new Fill();
  return function (context) {
    var color = evaluateColor(context);
    if (color === 'none') {
      return null;
    }
    fill.setColor(color);
    return fill;
  };
}

/**
 * @typedef {function(EvaluationContext):Stroke|null} StrokeEvaluator
 */

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} prefix The property prefix.
 * @param {ParsingContext} context The parsing context.
 * @return {StrokeEvaluator?} A function the evaluates to a stroke.
 */
function buildStroke(flatStyle, prefix, context) {
  var evaluateWidth = numberEvaluator(flatStyle, prefix + 'stroke-width', context);
  var evaluateColor = colorLikeEvaluator(flatStyle, prefix + 'stroke-color', context);
  if (!evaluateWidth && !evaluateColor) {
    return null;
  }
  var evaluateLineCap = stringEvaluator(flatStyle, prefix + 'stroke-line-cap', context);
  var evaluateLineJoin = stringEvaluator(flatStyle, prefix + 'stroke-line-join', context);
  var evaluateLineDash = numberArrayEvaluator(flatStyle, prefix + 'stroke-line-dash', context);
  var evaluateLineDashOffset = numberEvaluator(flatStyle, prefix + 'stroke-line-dash-offset', context);
  var evaluateMiterLimit = numberEvaluator(flatStyle, prefix + 'stroke-miter-limit', context);
  var stroke = new Stroke();
  return function (context) {
    if (evaluateColor) {
      var color = evaluateColor(context);
      if (color === 'none') {
        return null;
      }
      stroke.setColor(color);
    }
    if (evaluateWidth) {
      stroke.setWidth(evaluateWidth(context));
    }
    if (evaluateLineCap) {
      var lineCap = evaluateLineCap(context);
      if (lineCap !== 'butt' && lineCap !== 'round' && lineCap !== 'square') {
        throw new Error('Expected butt, round, or square line cap');
      }
      stroke.setLineCap(lineCap);
    }
    if (evaluateLineJoin) {
      var lineJoin = evaluateLineJoin(context);
      if (lineJoin !== 'bevel' && lineJoin !== 'round' && lineJoin !== 'miter') {
        throw new Error('Expected bevel, round, or miter line join');
      }
      stroke.setLineJoin(lineJoin);
    }
    if (evaluateLineDash) {
      stroke.setLineDash(evaluateLineDash(context));
    }
    if (evaluateLineDashOffset) {
      stroke.setLineDashOffset(evaluateLineDashOffset(context));
    }
    if (evaluateMiterLimit) {
      stroke.setMiterLimit(evaluateMiterLimit(context));
    }
    return stroke;
  };
}

/**
 * @typedef {function(EvaluationContext):Text} TextEvaluator
 */

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {ParsingContext} context The parsing context.
 * @return {TextEvaluator?} A function that evaluates to a text symbolizer.
 */
function buildText(flatStyle, context) {
  var prefix = 'text-';

  // Currently, an Array<string> may be used for rich text support.  This doesn't
  // work with our expression syntax where arrays of strings are interpreted as
  // call expressions.  To support rich text, we could add a 'strings' operator
  // where all the following arguments would be string values.
  var evaluateValue = stringEvaluator(flatStyle, prefix + 'value', context);
  if (!evaluateValue) {
    return null;
  }
  var evaluateFill = buildFill(flatStyle, prefix, context);
  var evaluateBackgroundFill = buildFill(flatStyle, prefix + 'background-', context);
  var evaluateStroke = buildStroke(flatStyle, prefix, context);
  var evaluateBackgroundStroke = buildStroke(flatStyle, prefix + 'background-', context);
  var evaluateFont = stringEvaluator(flatStyle, prefix + 'font', context);
  var evaluateMaxAngle = numberEvaluator(flatStyle, prefix + 'max-angle', context);
  var evaluateOffsetX = numberEvaluator(flatStyle, prefix + 'offset-x', context);
  var evaluateOffsetY = numberEvaluator(flatStyle, prefix + 'offset-y', context);
  var evaluateOverflow = booleanEvaluator(flatStyle, prefix + 'overflow', context);
  var evaluatePlacement = stringEvaluator(flatStyle, prefix + 'placement', context);
  var evaluateRepeat = numberEvaluator(flatStyle, prefix + 'repeat', context);
  var evaluateScale = sizeLikeEvaluator(flatStyle, prefix + 'scale', context);
  var evaluateRotateWithView = booleanEvaluator(flatStyle, prefix + 'rotate-with-view', context);
  var evaluateRotation = numberEvaluator(flatStyle, prefix + 'rotation', context);
  var evaluateAlign = stringEvaluator(flatStyle, prefix + 'align', context);
  var evaluateJustify = stringEvaluator(flatStyle, prefix + 'justify', context);
  var evaluateBaseline = stringEvaluator(flatStyle, prefix + 'baseline', context);
  var evaluatePadding = numberArrayEvaluator(flatStyle, prefix + 'padding', context);

  // The following properties are not currently settable
  var declutterMode = optionalDeclutterMode(flatStyle, prefix + 'declutter-mode');
  var text = new Text({
    declutterMode: declutterMode
  });
  return function (context) {
    text.setText(evaluateValue(context));
    if (evaluateFill) {
      text.setFill(evaluateFill(context));
    }
    if (evaluateBackgroundFill) {
      text.setBackgroundFill(evaluateBackgroundFill(context));
    }
    if (evaluateStroke) {
      text.setStroke(evaluateStroke(context));
    }
    if (evaluateBackgroundStroke) {
      text.setBackgroundStroke(evaluateBackgroundStroke(context));
    }
    if (evaluateFont) {
      text.setFont(evaluateFont(context));
    }
    if (evaluateMaxAngle) {
      text.setMaxAngle(evaluateMaxAngle(context));
    }
    if (evaluateOffsetX) {
      text.setOffsetX(evaluateOffsetX(context));
    }
    if (evaluateOffsetY) {
      text.setOffsetY(evaluateOffsetY(context));
    }
    if (evaluateOverflow) {
      text.setOverflow(evaluateOverflow(context));
    }
    if (evaluatePlacement) {
      var placement = evaluatePlacement(context);
      if (placement !== 'point' && placement !== 'line') {
        throw new Error('Expected point or line for text-placement');
      }
      text.setPlacement(placement);
    }
    if (evaluateRepeat) {
      text.setRepeat(evaluateRepeat(context));
    }
    if (evaluateScale) {
      text.setScale(evaluateScale(context));
    }
    if (evaluateRotateWithView) {
      text.setRotateWithView(evaluateRotateWithView(context));
    }
    if (evaluateRotation) {
      text.setRotation(evaluateRotation(context));
    }
    if (evaluateAlign) {
      var textAlign = evaluateAlign(context);
      if (textAlign !== 'left' && textAlign !== 'center' && textAlign !== 'right' && textAlign !== 'end' && textAlign !== 'start') {
        throw new Error('Expected left, right, center, start, or end for text-align');
      }
      text.setTextAlign(textAlign);
    }
    if (evaluateJustify) {
      var justify = evaluateJustify(context);
      if (justify !== 'left' && justify !== 'right' && justify !== 'center') {
        throw new Error('Expected left, right, or center for text-justify');
      }
      text.setJustify(justify);
    }
    if (evaluateBaseline) {
      var textBaseline = evaluateBaseline(context);
      if (textBaseline !== 'bottom' && textBaseline !== 'top' && textBaseline !== 'middle' && textBaseline !== 'alphabetic' && textBaseline !== 'hanging') {
        throw new Error('Expected bottom, top, middle, alphabetic, or hanging for text-baseline');
      }
      text.setTextBaseline(textBaseline);
    }
    if (evaluatePadding) {
      text.setPadding(evaluatePadding(context));
    }
    return text;
  };
}

/**
 * @typedef {function(EvaluationContext):import("../../style/Image.js").default} ImageEvaluator
 */

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {ParsingContext} context The parsing context.
 * @return {ImageEvaluator?} A function that evaluates to an image symbolizer.
 */
function buildImage(flatStyle, context) {
  if ('icon-src' in flatStyle) {
    return buildIcon(flatStyle, context);
  }
  if ('shape-points' in flatStyle) {
    return buildShape(flatStyle, context);
  }
  if ('circle-radius' in flatStyle) {
    return buildCircle(flatStyle, context);
  }
  return null;
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {ParsingContext} context The parsing context.
 * @return {ImageEvaluator} A function that evaluates to an image symbolizer.
 */
function buildIcon(flatStyle, context) {
  var prefix = 'icon-';

  // required property
  var srcName = prefix + 'src';
  var src = requireString(flatStyle[srcName], srcName);

  // settable properties
  var evaluateAnchor = coordinateEvaluator(flatStyle, prefix + 'anchor', context);
  var evaluateScale = sizeLikeEvaluator(flatStyle, prefix + 'scale', context);
  var evaluateOpacity = numberEvaluator(flatStyle, prefix + 'opacity', context);
  var evaluateDisplacement = coordinateEvaluator(flatStyle, prefix + 'displacement', context);
  var evaluateRotation = numberEvaluator(flatStyle, prefix + 'rotation', context);
  var evaluateRotateWithView = booleanEvaluator(flatStyle, prefix + 'rotate-with-view', context);

  // the remaining symbolizer properties are not currently settable
  var anchorOrigin = optionalIconOrigin(flatStyle, prefix + 'anchor-origin');
  var anchorXUnits = optionalIconAnchorUnits(flatStyle, prefix + 'anchor-x-units');
  var anchorYUnits = optionalIconAnchorUnits(flatStyle, prefix + 'anchor-y-units');
  var color = optionalColorLike(flatStyle, prefix + 'color');
  var crossOrigin = optionalString(flatStyle, prefix + 'cross-origin');
  var offset = optionalNumberArray(flatStyle, prefix + 'offset');
  var offsetOrigin = optionalIconOrigin(flatStyle, prefix + 'offset-origin');
  var width = optionalNumber(flatStyle, prefix + 'width');
  var height = optionalNumber(flatStyle, prefix + 'height');
  var size = optionalSize(flatStyle, prefix + 'size');
  var declutterMode = optionalDeclutterMode(flatStyle, prefix + 'declutter-mode');
  var icon = new Icon({
    src: src,
    anchorOrigin: anchorOrigin,
    anchorXUnits: anchorXUnits,
    anchorYUnits: anchorYUnits,
    color: color,
    crossOrigin: crossOrigin,
    offset: offset,
    offsetOrigin: offsetOrigin,
    height: height,
    width: width,
    size: size,
    declutterMode: declutterMode
  });
  return function (context) {
    if (evaluateOpacity) {
      icon.setOpacity(evaluateOpacity(context));
    }
    if (evaluateDisplacement) {
      icon.setDisplacement(evaluateDisplacement(context));
    }
    if (evaluateRotation) {
      icon.setRotation(evaluateRotation(context));
    }
    if (evaluateRotateWithView) {
      icon.setRotateWithView(evaluateRotateWithView(context));
    }
    if (evaluateScale) {
      icon.setScale(evaluateScale(context));
    }
    if (evaluateAnchor) {
      icon.setAnchor(evaluateAnchor(context));
    }
    return icon;
  };
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {ParsingContext} context The parsing context.
 * @return {ImageEvaluator} A function that evaluates to an icon symbolizer.
 */
function buildShape(flatStyle, context) {
  var prefix = 'shape-';

  // required property
  var pointsName = prefix + 'points';
  var radiusName = prefix + 'radius';
  var points = requireNumber(flatStyle[pointsName], pointsName);
  var radius = requireNumber(flatStyle[radiusName], radiusName);

  // settable properties
  var evaluateFill = buildFill(flatStyle, prefix, context);
  var evaluateStroke = buildStroke(flatStyle, prefix, context);
  var evaluateScale = sizeLikeEvaluator(flatStyle, prefix + 'scale', context);
  var evaluateDisplacement = coordinateEvaluator(flatStyle, prefix + 'displacement', context);
  var evaluateRotation = numberEvaluator(flatStyle, prefix + 'rotation', context);
  var evaluateRotateWithView = booleanEvaluator(flatStyle, prefix + 'rotate-with-view', context);

  // the remaining properties are not currently settable
  var radius2 = optionalNumber(flatStyle, prefix + 'radius2');
  var angle = optionalNumber(flatStyle, prefix + 'angle');
  var declutterMode = optionalDeclutterMode(flatStyle, prefix + 'declutter-mode');
  var shape = new RegularShape({
    points: points,
    radius: radius,
    radius2: radius2,
    angle: angle,
    declutterMode: declutterMode
  });
  return function (context) {
    if (evaluateFill) {
      shape.setFill(evaluateFill(context));
    }
    if (evaluateStroke) {
      shape.setStroke(evaluateStroke(context));
    }
    if (evaluateDisplacement) {
      shape.setDisplacement(evaluateDisplacement(context));
    }
    if (evaluateRotation) {
      shape.setRotation(evaluateRotation(context));
    }
    if (evaluateRotateWithView) {
      shape.setRotateWithView(evaluateRotateWithView(context));
    }
    if (evaluateScale) {
      shape.setScale(evaluateScale(context));
    }
    return shape;
  };
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {ParsingContext} context The parsing context.
 * @return {ImageEvaluator} A function that evaluates to a circle symbolizer.
 */
function buildCircle(flatStyle, context) {
  var prefix = 'circle-';

  // settable properties
  var evaluateFill = buildFill(flatStyle, prefix, context);
  var evaluateStroke = buildStroke(flatStyle, prefix, context);
  var evaluateRadius = numberEvaluator(flatStyle, prefix + 'radius', context);
  var evaluateScale = sizeLikeEvaluator(flatStyle, prefix + 'scale', context);
  var evaluateDisplacement = coordinateEvaluator(flatStyle, prefix + 'displacement', context);
  var evaluateRotation = numberEvaluator(flatStyle, prefix + 'rotation', context);
  var evaluateRotateWithView = booleanEvaluator(flatStyle, prefix + 'rotate-with-view', context);

  // the remaining properties are not currently settable
  var declutterMode = optionalDeclutterMode(flatStyle, prefix + 'declutter-mode');
  var circle = new Circle({
    radius: 5,
    // this is arbitrary, but required - the evaluated radius is used below
    declutterMode: declutterMode
  });
  return function (context) {
    if (evaluateRadius) {
      circle.setRadius(evaluateRadius(context));
    }
    if (evaluateFill) {
      circle.setFill(evaluateFill(context));
    }
    if (evaluateStroke) {
      circle.setStroke(evaluateStroke(context));
    }
    if (evaluateDisplacement) {
      circle.setDisplacement(evaluateDisplacement(context));
    }
    if (evaluateRotation) {
      circle.setRotation(evaluateRotation(context));
    }
    if (evaluateRotateWithView) {
      circle.setRotateWithView(evaluateRotateWithView(context));
    }
    if (evaluateScale) {
      circle.setScale(evaluateScale(context));
    }
    return circle;
  };
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} name The property name.
 * @param {ParsingContext} context The parsing context.
 * @return {import('../../expr/cpu.js').NumberEvaluator|undefined} The expression evaluator or undefined.
 */
function numberEvaluator(flatStyle, name, context) {
  if (!(name in flatStyle)) {
    return undefined;
  }
  var evaluator = buildExpression(flatStyle[name], NumberType, context);
  return function (context) {
    return requireNumber(evaluator(context), name);
  };
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} name The property name.
 * @param {ParsingContext} context The parsing context.
 * @return {import('../../expr/cpu.js').StringEvaluator?} The expression evaluator.
 */
function stringEvaluator(flatStyle, name, context) {
  if (!(name in flatStyle)) {
    return null;
  }
  var evaluator = buildExpression(flatStyle[name], StringType, context);
  return function (context) {
    return requireString(evaluator(context), name);
  };
}
function patternEvaluator(flatStyle, prefix, context) {
  var srcEvaluator = stringEvaluator(flatStyle, prefix + 'pattern-src', context);
  var offsetEvaluator = sizeEvaluator(flatStyle, prefix + 'pattern-offset', context);
  var patternSizeEvaluator = sizeEvaluator(flatStyle, prefix + 'pattern-size', context);
  var colorEvaluator = colorLikeEvaluator(flatStyle, prefix + 'color', context);
  return function (context) {
    return {
      src: srcEvaluator(context),
      offset: offsetEvaluator && offsetEvaluator(context),
      size: patternSizeEvaluator && patternSizeEvaluator(context),
      color: colorEvaluator && colorEvaluator(context)
    };
  };
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} name The property name.
 * @param {ParsingContext} context The parsing context.
 * @return {import('../../expr/cpu.js').BooleanEvaluator?} The expression evaluator.
 */
function booleanEvaluator(flatStyle, name, context) {
  if (!(name in flatStyle)) {
    return null;
  }
  var evaluator = buildExpression(flatStyle[name], BooleanType, context);
  return function (context) {
    var value = evaluator(context);
    if (typeof value !== 'boolean') {
      throw new Error("Expected a boolean for ".concat(name));
    }
    return value;
  };
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} name The property name.
 * @param {ParsingContext} context The parsing context.
 * @return {import('../../expr/cpu.js').ColorLikeEvaluator?} The expression evaluator.
 */
function colorLikeEvaluator(flatStyle, name, context) {
  if (!(name in flatStyle)) {
    return null;
  }
  var evaluator = buildExpression(flatStyle[name], ColorType | StringType, context);
  return function (context) {
    return requireColorLike(evaluator(context), name);
  };
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} name The property name.
 * @param {ParsingContext} context The parsing context.
 * @return {import('../../expr/cpu.js').NumberArrayEvaluator?} The expression evaluator.
 */
function numberArrayEvaluator(flatStyle, name, context) {
  if (!(name in flatStyle)) {
    return null;
  }
  var evaluator = buildExpression(flatStyle[name], NumberArrayType, context);
  return function (context) {
    return requireNumberArray(evaluator(context), name);
  };
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} name The property name.
 * @param {ParsingContext} context The parsing context.
 * @return {import('../../expr/cpu.js').CoordinateEvaluator?} The expression evaluator.
 */
function coordinateEvaluator(flatStyle, name, context) {
  if (!(name in flatStyle)) {
    return null;
  }
  var evaluator = buildExpression(flatStyle[name], NumberArrayType, context);
  return function (context) {
    var array = requireNumberArray(evaluator(context), name);
    if (array.length !== 2) {
      throw new Error("Expected two numbers for ".concat(name));
    }
    return array;
  };
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} name The property name.
 * @param {ParsingContext} context The parsing context.
 * @return {import('../../expr/cpu.js').SizeEvaluator?} The expression evaluator.
 */
function sizeEvaluator(flatStyle, name, context) {
  if (!(name in flatStyle)) {
    return null;
  }
  var evaluator = buildExpression(flatStyle[name], NumberArrayType, context);
  return function (context) {
    return requireSize(evaluator(context), name);
  };
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} name The property name.
 * @param {ParsingContext} context The parsing context.
 * @return {import('../../expr/cpu.js').SizeLikeEvaluator?} The expression evaluator.
 */
function sizeLikeEvaluator(flatStyle, name, context) {
  if (!(name in flatStyle)) {
    return null;
  }
  var evaluator = buildExpression(flatStyle[name], NumberArrayType | NumberType, context);
  return function (context) {
    return requireSizeLike(evaluator(context), name);
  };
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} property The symbolizer property.
 * @return {number|undefined} A number or undefined.
 */
function optionalNumber(flatStyle, property) {
  var value = flatStyle[property];
  if (value === undefined) {
    return undefined;
  }
  if (typeof value !== 'number') {
    throw new Error("Expected a number for ".concat(property));
  }
  return value;
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} property The symbolizer property.
 * @return {import("../../size.js").Size|undefined} A size or undefined.
 */
function optionalSize(flatStyle, property) {
  var encoded = flatStyle[property];
  if (encoded === undefined) {
    return undefined;
  }
  if (typeof encoded === 'number') {
    return toSize(encoded);
  }
  if (!Array.isArray(encoded)) {
    throw new Error("Expected a number or size array for ".concat(property));
  }
  if (encoded.length !== 2 || typeof encoded[0] !== 'number' || typeof encoded[1] !== 'number') {
    throw new Error("Expected a number or size array for ".concat(property));
  }
  return encoded;
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} property The symbolizer property.
 * @return {string|undefined} A string or undefined.
 */
function optionalString(flatStyle, property) {
  var encoded = flatStyle[property];
  if (encoded === undefined) {
    return undefined;
  }
  if (typeof encoded !== 'string') {
    throw new Error("Expected a string for ".concat(property));
  }
  return encoded;
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} property The symbolizer property.
 * @return {import("../../style/Icon.js").IconOrigin|undefined} An icon origin or undefined.
 */
function optionalIconOrigin(flatStyle, property) {
  var encoded = flatStyle[property];
  if (encoded === undefined) {
    return undefined;
  }
  if (encoded !== 'bottom-left' && encoded !== 'bottom-right' && encoded !== 'top-left' && encoded !== 'top-right') {
    throw new Error("Expected bottom-left, bottom-right, top-left, or top-right for ".concat(property));
  }
  return encoded;
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} property The symbolizer property.
 * @return {import("../../style/Icon.js").IconAnchorUnits|undefined} Icon anchor units or undefined.
 */
function optionalIconAnchorUnits(flatStyle, property) {
  var encoded = flatStyle[property];
  if (encoded === undefined) {
    return undefined;
  }
  if (encoded !== 'pixels' && encoded !== 'fraction') {
    throw new Error("Expected pixels or fraction for ".concat(property));
  }
  return encoded;
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} property The symbolizer property.
 * @return {Array<number>|undefined} An array of numbers or undefined.
 */
function optionalNumberArray(flatStyle, property) {
  var encoded = flatStyle[property];
  if (encoded === undefined) {
    return undefined;
  }
  return requireNumberArray(encoded, property);
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} property The symbolizer property.
 * @return {import('../../style/Style.js').DeclutterMode} Icon declutter mode.
 */
function optionalDeclutterMode(flatStyle, property) {
  var encoded = flatStyle[property];
  if (encoded === undefined) {
    return undefined;
  }
  if (typeof encoded !== 'string') {
    throw new Error("Expected a string for ".concat(property));
  }
  if (encoded !== 'declutter' && encoded !== 'obstacle' && encoded !== 'none') {
    throw new Error("Expected declutter, obstacle, or none for ".concat(property));
  }
  return encoded;
}

/**
 * @param {FlatStyle} flatStyle The flat style.
 * @param {string} property The symbolizer property.
 * @return {string|Array<number>|undefined} A string or an array of color values or undefined.
 */
function optionalColorLike(flatStyle, property) {
  var encoded = flatStyle[property];
  if (encoded === undefined) {
    return undefined;
  }
  return requireColorLike(encoded, property);
}

/**
 * @param {any} value The value.
 * @param {string} property The property.
 * @return {Array<number>} An array of numbers.
 */
function requireNumberArray(value, property) {
  if (!Array.isArray(value)) {
    throw new Error("Expected an array for ".concat(property));
  }
  var length = value.length;
  for (var i = 0; i < length; ++i) {
    if (typeof value[i] !== 'number') {
      throw new Error("Expected an array of numbers for ".concat(property));
    }
  }
  return value;
}

/**
 * @param {any} value The value.
 * @param {string} property The property.
 * @return {string} A string.
 */
function requireString(value, property) {
  if (typeof value !== 'string') {
    throw new Error("Expected a string for ".concat(property));
  }
  return value;
}

/**
 * @param {any} value The value.
 * @param {string} property The property.
 * @return {number} A number.
 */
function requireNumber(value, property) {
  if (typeof value !== 'number') {
    throw new Error("Expected a number for ".concat(property));
  }
  return value;
}

/**
 * @param {any} value The value.
 * @param {string} property The property.
 * @return {Array<number>|string} A color.
 */
function requireColorLike(value, property) {
  if (typeof value === 'string') {
    return value;
  }
  var array = requireNumberArray(value, property);
  var length = array.length;
  if (length < 3 || length > 4) {
    throw new Error("Expected a color with 3 or 4 values for ".concat(property));
  }
  return array;
}

/**
 * @param {any} value The value.
 * @param {string} property The property.
 * @return {Array<number>} A number or an array of two numbers.
 */
function requireSize(value, property) {
  var size = requireNumberArray(value, property);
  if (size.length !== 2) {
    throw new Error("Expected an array of two numbers for ".concat(property));
  }
  return size;
}

/**
 * @param {any} value The value.
 * @param {string} property The property.
 * @return {number|Array<number>} A number or an array of two numbers.
 */
function requireSizeLike(value, property) {
  if (typeof value === 'number') {
    return value;
  }
  return requireSize(value, property);
}